// Single source for the workbench's object-type vocabulary — every `obj_type`
// a project's navigation can carry, with its singular/plural label, its
// material icon and the order breakdowns list them in.
//
// Each surface used to keep its own partial copy of this list, and a type that
// was missing from one copy silently vanished from that surface: the Overview
// "By Type" breakdown filtered through `['D','T','V','G','R','W','P','C']`, so
// Smart Pages, Visual Schemas, Business Areas and External Websites were never
// counted — and because the overall Specification/Model bars were derived from
// the same (short) list, their totals disagreed with the percentage above them.
// Keep new obj_types in sync with `ProjectService.MODEL_TYPE_MAPPING`.
import type { TypeProgress } from '@/store/projectStore'
import { checkBatchModelStatus } from '@/api/generateapp'
import { objectCatalog } from '@/api/designers'

export const OBJ_TYPE_SINGULAR: Record<string, string> = {
  '-': 'Folder',
  T: 'Transaction',
  S: 'Smart Page',
  V: 'Visual',
  M: 'Visual Schema',
  D: 'Dashboard',
  R: 'Report',
  G: 'General Process',
  W: 'Workflow',
  A: 'Business Area',
  X: 'External Website',
  P: 'Process Data',
  F: 'Foresight',
  K: 'Document Repository',
  E: 'Document Form',
  U: 'Wizard',
  J: 'Integration Flow',
}

export const OBJ_TYPE_PLURAL: Record<string, string> = {
  '-': 'Folders',
  T: 'Transactions',
  S: 'Smart Pages',
  V: 'Visuals',
  M: 'Visual Schemas',
  D: 'Dashboards',
  R: 'Reports',
  G: 'Processes',
  W: 'Workflows',
  A: 'Business Areas',
  X: 'External Websites',
  P: 'Process Data',
  F: 'Foresight Models',
  K: 'Document Repositories',
  E: 'Document Forms',
  U: 'Wizards',
  J: 'Integration Flows',
}

/** Material icon name per type (the icon set the workbench Overview uses). */
export const OBJ_TYPE_ICON: Record<string, string> = {
  '-': 'folder',
  T: 'receipt_long',
  S: 'web',
  V: 'pie_chart',
  M: 'schema',
  D: 'dashboard',
  R: 'bar_chart',
  G: 'settings',
  W: 'account_tree',
  A: 'psychology',
  X: 'language',
  P: 'storage',
  F: 'insights',
  K: 'folder_shared',
  E: 'article',
  U: 'auto_fix_high',
  J: 'account_tree',
}

/** Display order for per-type breakdowns. Unknown types sort after these. */
export const OBJ_TYPE_ORDER = ['T', 'S', 'V', 'M', 'D', 'R', 'G', 'K', 'E', 'U', 'J', 'W', 'A', 'X', 'P', 'F']

/** Types the object catalog can enumerate from disk (its `_MODEL_DIRS` keys).
    Anything else (X, P) is only known through the navigation. */
export const CATALOG_TYPES = 'T,S,V,D,M,A,G,R,W,K,E,U'

export function objTypePlural(objType: string): string {
  return OBJ_TYPE_PLURAL[objType] || objType
}

export function objTypeSingular(objType: string): string {
  return OBJ_TYPE_SINGULAR[objType] || objType
}

export function objTypeIcon(objType: string): string {
  return OBJ_TYPE_ICON[objType] || 'category'
}

/** The navigation-node fields the breakdown needs. */
export interface NavProgressItem {
  obj_name: string
  obj_type: string
  functional_specs?: string
}

/** An object that exists as a MODEL FILE on disk (the object catalog), whether
    or not the application menu points at it. */
export interface CatalogProgressItem {
  obj_name: string
  obj_type: string
}

/**
 * Per-type spec/model breakdown over everything the project HAS.
 *
 * Two sources, unioned per object:
 *  - `items` — the application-menu nav nodes (they carry the functional spec);
 *  - `catalog` — model files on disk. Reports, processes, visuals, schemas and
 *    business areas authored in a designer are real objects even before anyone
 *    adds them to the menu; counting only nav made them invisible here.
 *
 * Every type present is reported — the known ones in `OBJ_TYPE_ORDER`, then
 * anything unrecognised (labelled by its raw code) so a new obj_type shows up
 * as an unnamed row rather than disappearing from the counts. Folders ('-')
 * are not objects and are always excluded.
 */
export function computeTypeProgress(
  items: NavProgressItem[],
  statuses: Record<string, { model_exists: boolean }>,
  catalog: CatalogProgressItem[] = [],
): TypeProgress[] {
  // type → object key → what that object has. Names are matched case-
  // insensitively: a nav node and its model file can differ in casing (the
  // backend resolves models case-tolerantly for exactly this reason).
  const byType = new Map<string, Map<string, { spec: boolean; model: boolean }>>()
  const entry = (objType: string, objName: string) => {
    let objs = byType.get(objType)
    if (!objs) { objs = new Map(); byType.set(objType, objs) }
    const key = (objName || '').trim().toLowerCase()
    let rec = objs.get(key)
    if (!rec) { rec = { spec: false, model: false }; objs.set(key, rec) }
    return rec
  }

  for (const item of items) {
    const t = item?.obj_type
    if (!t || t === '-' || !item.obj_name) continue
    const rec = entry(t, item.obj_name)
    if (item.functional_specs?.trim()) rec.spec = true
    if (statuses?.[item.obj_name]?.model_exists) rec.model = true
  }
  for (const obj of catalog) {
    const t = obj?.obj_type
    if (!t || t === '-' || !obj.obj_name) continue
    // Being in the catalog IS the model file existing.
    entry(t, obj.obj_name).model = true
  }

  const known = OBJ_TYPE_ORDER.filter((t) => byType.has(t))
  const unknown = [...byType.keys()].filter((t) => !OBJ_TYPE_ORDER.includes(t)).sort()
  return [...known, ...unknown].map((t) => {
    const objs = [...byType.get(t)!.values()]
    return {
      type: t,
      label: objTypePlural(t),
      total: objs.length,
      specDone: objs.filter((o) => o.spec).length,
      modelDone: objs.filter((o) => o.model).length,
    }
  })
}

export interface ProjectProgress {
  /** Per-nav-object model existence — the Prototype tree's status dots. */
  statuses: Record<string, { model_exists: boolean }>
  typeProgress: TypeProgress[]
  /** 0-100, or null when the project has no objects at all. */
  specPct: number | null
  modelPct: number | null
}

const EMPTY_PROGRESS: ProjectProgress = {
  statuses: {}, typeProgress: [], specPct: null, modelPct: null,
}

/**
 * The project's generation progress, computed ONCE for every surface that
 * shows it (workbench Overview + Prototype tab). Both read the same store, so
 * computing it two different ways made the numbers change as you switched tab.
 *
 * The catalog half is best-effort: if it fails, the breakdown degrades to the
 * navigation-only view rather than the whole panel disappearing.
 */
export async function loadProjectProgress(
  project: string, navItems: NavProgressItem[],
): Promise<ProjectProgress> {
  if (!project) return EMPTY_PROGRESS
  const objects = navItems.map((n) => ({ obj_name: n.obj_name, obj_type: n.obj_type }))
  const [statusRes, catRes] = await Promise.allSettled([
    objects.length ? checkBatchModelStatus(project, objects) : Promise.resolve({ statuses: {} }),
    objectCatalog(project, CATALOG_TYPES),
  ])
  const statuses = statusRes.status === 'fulfilled' ? (statusRes.value.statuses || {}) : {}
  const catalog = catRes.status === 'fulfilled' ? (catRes.value.objects || []) : []
  const typeProgress = computeTypeProgress(navItems, statuses, catalog)
  const total = typeProgress.reduce((s, t) => s + t.total, 0)
  if (total === 0) return { ...EMPTY_PROGRESS, statuses }
  const pct = (n: number) => Math.round((n / total) * 100)
  return {
    statuses,
    typeProgress,
    specPct: pct(typeProgress.reduce((s, t) => s + t.specDone, 0)),
    modelPct: pct(typeProgress.reduce((s, t) => s + t.modelDone, 0)),
  }
}
